import {
  BasicFilter,
  FilterWithJobStage,
  FilterWithTimeRange,
} from 'common/models/Reporting/AnalyticsFilterData';
import {
  KpiDashboard,
  OpenJob,
  StageTotalObj,
  TopClientsByActiviyRecord,
  TopClientsByFeesRecord,
  TopClientsByJobRecord,
  TopClientsByRatiosRecord,
  TopContactsByActiviyRecord,
  TopContactsByFeesRecord,
  TopContactsByJobRecord,
  TopContactsByRatiosRecord,
  KpiOverview,
  OpportunitiesByTypeRecord,
  OpportunitiesByStageRecord,
  JobsByConsultantRecord,
  JobsBySourceRecord,
  JobsByStageRecord,
  ApplicantsBySourceRecord,
  AveragePermFeesRecord,
  AverageContractMarginRecord,
  PlacementsAverageByConsultantRecord,
  PlacementsContractFinancialsOverviewRecord,
  PlacementsContractFinancialsTrendRecord,
  PlacementQtyBySourceRecord,
  PlacementFeesBySourceRecord,
  TypeActualObj,
  PerformanceActivityRatioRecord,
  PerformanceActivityRatioTrendsRecord,
  PerformancePhoneTimeRecord,
  ActivitiesGraphData,
  JobPipelineGraphData,
  MetricFilterRequest,
} from '../common/models/Reporting/BusinessIntelligence';
import { ErrorHandler, NameIdObj, RequestOptions } from '../common/models/GenericTypes';
import { Get, Post } from '../util/HttpUtils';
import { Job } from 'common/models/Jobs/Job';

export async function GetMyOpenJobs(errorHandler?: ErrorHandler) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Get<OpenJob[]>('api/BusinessIntelligence/MyOpenJobs', options);
}
export async function GetKpiDashboardData(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<KpiDashboard[]>('api/BusinessIntelligence/KPIDashboard', options, filterData);
}

export async function GetKpiOverviewData(
  filterData: FilterWithTimeRange,
  kioskToken: string | null,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  let url = 'api/BusinessIntelligence/KPIOverview';
  if (kioskToken) {
    url = `api/DashboardKiosks/${kioskToken}/KPIOverview`;
  }

  return Post<KpiOverview>(url, options, filterData);
}

export async function TopClientsByJobs(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<TopClientsByJobRecord[]>(
    'api/BusinessIntelligence/TopClientsByJobs',
    options,
    filterData
  );
}

export async function TopClientsByFees(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<TopClientsByFeesRecord[]>(
    'api/BusinessIntelligence/TopClientsByFees',
    options,
    filterData
  );
}

export async function TopClientsByActivity(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<TopClientsByActiviyRecord[]>(
    'api/BusinessIntelligence/TopClientsByActivity',
    options,
    { ...filterData, sortColumn: 'Placements' }
  );
}

export async function TopClientsByRatios(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<TopClientsByRatiosRecord[]>('api/BusinessIntelligence/TopClientsByRatios', options, {
    ...filterData,
    sortColumn: 'SubmissionsToPlacementsRatio',
  });
}

export async function TopContactsByJobs(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<TopContactsByJobRecord[]>(
    'api/BusinessIntelligence/TopContactsByJobs',
    options,
    filterData
  );
}

export async function TopContactsByFees(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<TopContactsByFeesRecord[]>(
    'api/BusinessIntelligence/TopContactsByFees',
    options,
    filterData
  );
}

export async function TopContactsByActivity(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<TopContactsByActiviyRecord[]>(
    'api/BusinessIntelligence/TopContactsByActivity',
    options,
    { ...filterData, sortColumn: 'Placements' }
  );
}

export async function TopContactsByRatios(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<TopContactsByRatiosRecord[]>(
    'api/BusinessIntelligence/TopContactsByRatios',
    options,
    { ...filterData, sortColumn: 'SubmissionsToPlacementsRatio' }
  );
}

export async function GetOpportunitiesByType(filterData: BasicFilter, errorHandler?: ErrorHandler) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<OpportunitiesByTypeRecord[]>(
    'api/BusinessIntelligence/OpportunitiesByType',
    options,
    filterData
  );
}

export async function GetOpportunitiesByStage(
  filterData: BasicFilter,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<OpportunitiesByStageRecord[]>(
    'api/BusinessIntelligence/OpportunitiesByStage',
    options,
    filterData
  );
}

export async function GetNewJobsByConsultant(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<JobsByConsultantRecord[]>(
    'api/BusinessIntelligence/JobsByConsultantNew',
    options,
    filterData
  );
}

export async function GetOpenJobsByConsultant(
  filterData: FilterWithJobStage,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<JobsByConsultantRecord[]>(
    'api/BusinessIntelligence/JobsByConsultantOpen',
    options,
    filterData
  );
}

export async function GetClosedJobsByConsultant(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<JobsByConsultantRecord[]>(
    'api/BusinessIntelligence/JobsByConsultantClosed',
    options,
    filterData
  );
}

export async function GetPlacedJobsByConsultant(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<JobsByConsultantRecord[]>(
    'api/BusinessIntelligence/JobsByConsultantPlaced',
    options,
    filterData
  );
}

export async function GetJobsBySource(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<JobsBySourceRecord[]>('api/BusinessIntelligence/JobsBySource', options, filterData);
}

export async function GetJobsByStage(filterData: BasicFilter, errorHandler?: ErrorHandler) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<JobsByStageRecord[]>('api/BusinessIntelligence/JobsByStage', options, filterData);
}

export async function GetApplicantsBySource(filterData: BasicFilter, errorHandler?: ErrorHandler) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<ApplicantsBySourceRecord[]>(
    'api/BusinessIntelligence/ApplicantsBySource',
    options,
    filterData
  );
}

export async function GetPlacementsAveragePermFees(
  filterData: BasicFilter,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<AveragePermFeesRecord[]>(
    'api/BusinessIntelligence/AveragePermFees',
    options,
    filterData
  );
}

export async function GetPlacementsAverageDailyContractMargin(
  filterData: BasicFilter,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<AverageContractMarginRecord[]>(
    'api/BusinessIntelligence/AverageDailyContractMargin',
    options,
    filterData
  );
}

export async function GetPlacementsAverageHourlyContractMargin(
  filterData: BasicFilter,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<AverageContractMarginRecord[]>(
    'api/BusinessIntelligence/AverageHourlyContractMargin',
    options,
    filterData
  );
}

export async function GetPlacementsAverageByConsultant(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<PlacementsAverageByConsultantRecord[]>(
    'api/BusinessIntelligence/AverageFeesByConsultant',
    options,
    filterData
  );
}

export async function GetContractFinancialsOverview(
  filterData: BasicFilter,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<PlacementsContractFinancialsOverviewRecord[]>(
    'api/BusinessIntelligence/ContractFinancialsOverview',
    options,
    filterData
  );
}

export async function GetContractFinancialsTrend(
  filterData: BasicFilter,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<PlacementsContractFinancialsTrendRecord[]>(
    'api/BusinessIntelligence/ContractFinancialsTrend',
    options,
    filterData
  );
}

export async function GetContractorBookByFees(
  filterData: BasicFilter,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<StageTotalObj[]>(
    'api/BusinessIntelligence/ContractorBookByFees',
    options,
    filterData
  );
}

export async function GetContractorBookByQuantity(
  filterData: BasicFilter,
  kioskToken: string | null = null,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  let url = 'api/BusinessIntelligence/ContractorBookByQty';
  if (kioskToken) {
    url = `api/DashboardKiosks/${kioskToken}/ContractorBookByQty`;
  }

  return Post<StageTotalObj[]>(url, options, filterData);
}

export async function GetPlacementQuantityBySource(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<PlacementQtyBySourceRecord[]>(
    'api/BusinessIntelligence/PlacementQtyBySource',
    options,
    filterData
  );
}

export async function GetPlacementFeesBySource(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<PlacementFeesBySourceRecord[]>(
    'api/BusinessIntelligence/PlacementFeesBySource',
    options,
    filterData
  );
}

export async function GetPerformanceActivitiesData(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<TypeActualObj[]>('api/BusinessIntelligence/Activities', options, filterData);
}

export async function GetPerformanceActivityRatiosData(
  filterData: FilterWithTimeRange,
  kioskToken?: string | null,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  let url = 'api/BusinessIntelligence/ActivityRatios';
  if (kioskToken) {
    url = `api/DashboardKiosks/${kioskToken}/ActivityRatios`;
  }

  return Post<PerformanceActivityRatioRecord[]>(url, options, { ...filterData, sortColumn: '' });
}

export async function GetPerformanceActivityRatioTrendData(
  filterData: BasicFilter,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<PerformanceActivityRatioTrendsRecord[]>(
    'api/BusinessIntelligence/ActivityRatiosTrends',
    options,
    filterData
  );
}

export async function GetTotalFeesByConsultantData(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<any[]>('api/BusinessIntelligence/TotalFeesByConsultant', options, filterData);
}

export async function GetPhoneTimeData(
  filterData: FilterWithTimeRange,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  return Post<PerformancePhoneTimeRecord[]>(
    'api/BusinessIntelligence/PhoneTime',
    options,
    filterData
  );
}

export async function GetDashboardActivitiesGraphData(
  consultantId: number,
  startDate: string,
  endDate: string,
  kioskToken: string | null = null,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  const params = new URLSearchParams();
  params.set('consultantId', consultantId.toString());
  params.set('startDate', startDate);
  params.set('endDate', endDate);

  let url = `api/BusinessIntelligence/DashboardActivitiesGraph?${params}`;
  if (kioskToken) {
    url = `api/DashboardKiosks/${kioskToken}/DashboardActivitiesGraph`;
  }
  return Get<ActivitiesGraphData>(url, options);
}

export async function GetDashboardJobPipelineGraphData(
  filterData: BasicFilter,
  kioskToken: string | null,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  let url = 'api/BusinessIntelligence/DashboardJobPipelineGraph';
  if (kioskToken) {
    url = `api/DashboardKiosks/${kioskToken}/DashboardJobPipelineGraph`;
  }

  return Post<JobPipelineGraphData[]>(url, options, filterData);
}

export async function GetDataForOpenJobsGraph(
  filter: BasicFilter,
  kioskToken: string | null,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };

  let url = 'api/BusinessIntelligence/DataForOpenJobsGraph';
  if (kioskToken) {
    url = `api/DashboardKiosks/${kioskToken}/DataForOpenJobsGraph`;
  }
  return Post<Job[]>(url, options, filter);
}

export async function GetActivityTypes(errorHandler?: ErrorHandler) {
  const options: RequestOptions = {
    errorHandler,
  };

  const url = 'api/BusinessIntelligence/ActivityTypes';

  return Get<NameIdObj[]>(url, options);
}

export async function GetDashboardActivitiesGraphDatav2(
  request: MetricFilterRequest,
  kioskToken: string | null,
  errorHandler?: ErrorHandler
) {
  const options: RequestOptions = {
    errorHandler,
  };
  let url = 'api/BusinessIntelligence/DashboardActivitiesGraph/v2';
  if (kioskToken) {
    url = `api/DashboardKiosks/${kioskToken}/DashboardActivitiesGraph`;
  }
  return Post<ActivitiesGraphData[]>(url, options, request);
}
